import irmHeader from 'assets/images/irm-header.png';
import { patientBarcodeStrip, barcodeScriptBlock, resolveCoupleSpouseCodes } from 'utils/printBarcodes';

// ── Shared helpers (this trio of OPD documents is small/fresh enough to
// share helpers in one file, unlike pharmacy's many independently-evolved
// print files which duplicate per repo convention) ──

const PRIMARY = '#00798E';
const LIGHT_BG = '#e0f4f7';
const BORDER = '#b2d8de';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function feeLabel(line) {
  return line.fee_type === 'REGISTRATION' ? 'Registration Fee' : 'Consultation Fee';
}

const MODE_STYLE = {
  CASH: { bg: '#e8f5e9', color: '#2e7d32' },
  UPI: { bg: '#e3f2fd', color: '#1565c0' },
  CARD: { bg: '#f3e5f5', color: '#6a1b9a' },
  ONLINE: { bg: '#fff3e0', color: '#e65100' },
};

function openPrintWindow(title, toolbarLabel, bodyHtml) {
  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:${PRIMARY};color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">${toolbarLabel}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:${PRIMARY};border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:484px;margin:0 auto">${bodyHtml}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}

function letterhead(docTitle) {
  return `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">${docTitle}</div>
      </div>
    </div>`;
}

/**
 * Patient/Doctor info block (two columns) — the barcode strip goes in its
 * own full-width row underneath (see barcodeRow()), not squeezed into a
 * third column here.
 */
function patientDoctorBlock(bill) {
  return `
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER}">
      <tr>
        <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Patient</div>
          <div style="font-weight:700;font-size:9pt">${bill.contactInfo?.first_name ? [bill.contactInfo.first_name, bill.contactInfo.middle_name, bill.contactInfo.last_name].filter(Boolean).join(' ') : `Patient #${bill.irm_contacts_id}`}</div>
          ${bill.contactInfo?.external_code ? `<div style="font-size:7.5pt;color:#555">Patient ID: ${bill.contactInfo.external_code}</div>` : ''}
        </td>
        <td style="width:50%;padding:5px 8px;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Doctor</div>
          <div style="font-weight:700;font-size:9pt">${bill.doctorInfo?.name || `Doctor #${bill.irm_users_id}`}</div>
          <div style="font-size:7.5pt;color:#555">Bill Date: ${fmtDate(bill.bill_date)}</div>
        </td>
      </tr>
    </table>`;
}

/**
 * Patient ID / Couple ID / Spouse ID / Bill No barcodes, side by side in a
 * single full-width row underneath the Patient/Doctor block.
 */
function barcodeRow(bill, idPrefix) {
  const { coupleCode, spouseCode } = resolveCoupleSpouseCodes(bill.contactInfo);
  const { html, initScript } = patientBarcodeStrip({
    idPrefix,
    direction: 'row',
    patientCode: bill.contactInfo?.external_code,
    coupleCode,
    spouseCode,
    documentCode: bill.bill_no,
    documentLabel: 'Bill No',
  });
  if (!html) return '';
  return `
    <div style="border:1.5px solid ${BORDER};border-top:none;padding:6px 8px">${html}</div>
    ${barcodeScriptBlock(initScript)}`;
}

function feeLinesTable(bill) {
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;
  const rows = (bill.lines || [])
    .map(
      line => `<tr>
        <td style="${C}">${feeLabel(line)}${line.is_waived === 'Y' ? `<div style="font-size:6.5pt;color:#8d6e63;font-style:italic">Waived — ${line.waived_reason || ''}</div>` : ''}</td>
        <td style="${C}text-align:right;font-family:monospace">${Number(line.amount).toFixed(2)}</td>
      </tr>`,
    )
    .join('');
  return `
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;margin-top:4px;table-layout:fixed">
      <tr><th style="${CH}text-align:left">Description</th><th style="${CH}width:35%;text-align:right">Amount (₹)</th></tr>
      ${rows}
      <tr style="background:${LIGHT_BG}"><td style="${C}font-weight:800">Total</td><td style="${C}text-align:right;font-family:monospace;font-weight:800">${Number(bill.total_amount).toFixed(2)}</td></tr>
      <tr><td style="${C}color:#2e7d32">Paid</td><td style="${C}text-align:right;font-family:monospace;color:#2e7d32">${Number(bill.paid_amount).toFixed(2)}</td></tr>
      ${Number(bill.balance_amount) > 0 ? `<tr><td style="${C}color:#c62828;font-weight:700">Balance Due</td><td style="${C}text-align:right;font-family:monospace;font-weight:700;color:#c62828">${Number(bill.balance_amount).toFixed(2)}</td></tr>` : ''}
    </table>`;
}

function signatureFooter() {
  return `
    <table style="width:100%;border-collapse:collapse;margin-top:6px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:70%;padding:4px 7px;border-right:1px solid ${BORDER};text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
        <td style="width:30%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:16px">
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
    </table>`;
}

/** The itemized OPD bill — fee breakdown + total/paid/balance. */
export function printOpdBill(bill) {
  if (!bill) return;
  const body = `
    ${letterhead('OPD BILL')}
    <div style="text-align:right;font-size:8pt;font-weight:700;color:${PRIMARY};margin-bottom:3px">Bill No: ${bill.bill_no || `#${bill.id}`}</div>
    ${patientDoctorBlock(bill)}
    ${barcodeRow(bill, 'bc-bill')}
    ${feeLinesTable(bill)}
    ${signatureFooter()}
  `;
  openPrintWindow('OPD Bill', `OPD Bill — ${bill.bill_no || `#${bill.id}`}`, body);
}

/**
 * Formal statement-style invoice — same fee breakdown as the Bill, plus the
 * full payment history, meant as the definitive billing document for
 * records (vs. the quick counter Bill and the single-payment Receipt).
 */
export function printOpdInvoice(bill) {
  if (!bill) return;
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;
  const paymentRows = (bill.payments || [])
    .filter(p => p.status === 'PAID')
    .map(
      p => `<tr>
        <td style="${C}">${fmtDateTime(p.paid_at)}</td>
        <td style="${C}"><span style="padding:2px 8px;border-radius:3px;font-size:7pt;font-weight:700;background:${(MODE_STYLE[p.payment_mode] || {}).bg || '#eee'};color:${(MODE_STYLE[p.payment_mode] || {}).color || '#555'}">${p.payment_mode}</span></td>
        <td style="${C}font-family:monospace">${p.reference_no || '—'}</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:700">${Number(p.amount).toFixed(2)}</td>
      </tr>`,
    )
    .join('');

  const body = `
    ${letterhead('TAX INVOICE')}
    <div style="text-align:right;font-size:8pt;font-weight:700;color:${PRIMARY};margin-bottom:3px">Invoice No: ${bill.bill_no || `#${bill.id}`} &nbsp;|&nbsp; Status: ${bill.status}</div>
    ${patientDoctorBlock(bill)}
    ${barcodeRow(bill, 'bc-inv')}
    ${feeLinesTable(bill)}
    ${
      paymentRows
        ? `<div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin:6px 0 2px">Payment History</div>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};table-layout:fixed">
      <tr><th style="${CH}text-align:left">Date</th><th style="${CH}text-align:left">Mode</th><th style="${CH}text-align:left">Reference</th><th style="${CH}text-align:right">Amount (₹)</th></tr>
      ${paymentRows}
    </table>`
        : ''
    }
    <div style="margin-top:4px;font-size:6.5pt;color:#888;font-style:italic">Medical consultation services — GST not applicable.</div>
    ${signatureFooter()}
  `;
  openPrintWindow('OPD Invoice', `OPD Invoice — ${bill.bill_no || `#${bill.id}`}`, body);
}

/** Proof-of-payment receipt for one specific payment on the bill. */
export function printOpdReceipt(bill, payment) {
  if (!bill || !payment) return;
  const modeStyle = MODE_STYLE[payment.payment_mode] || { bg: '#f5f5f5', color: '#757575' };
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;
  const body = `
    ${letterhead('PAYMENT RECEIPT')}
    <div style="text-align:right;font-size:8pt;font-weight:700;color:${PRIMARY};margin-bottom:3px">Against Bill: ${bill.bill_no || `#${bill.id}`}</div>
    ${patientDoctorBlock(bill)}
    ${barcodeRow(bill, 'bc-rcpt')}
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="color:#777;padding:4px 8px;white-space:nowrap">Date</td><td style="font-weight:700;padding:4px 8px">${fmtDateTime(payment.paid_at)}</td>
        <td style="color:#777;padding:4px 8px;white-space:nowrap">Mode</td><td style="padding:4px 8px"><span style="padding:2px 8px;border-radius:3px;font-size:7pt;font-weight:700;background:${modeStyle.bg};color:${modeStyle.color}">${payment.payment_mode}</span></td>
      </tr>
      ${payment.reference_no ? `<tr><td style="color:#777;padding:0 8px 4px;white-space:nowrap">Reference</td><td colspan="3" style="font-weight:600;font-family:monospace;padding:0 8px 4px">${payment.reference_no}</td></tr>` : ''}
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;table-layout:fixed">
      <tr><th style="${CH}text-align:left">Description</th><th style="${CH}width:35%;text-align:right">Amount (₹)</th></tr>
      <tr style="background:${LIGHT_BG}"><td style="${C}font-weight:700">Amount Received</td><td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:11pt;color:${PRIMARY}">${Number(payment.amount).toFixed(2)}</td></tr>
      ${Number(bill.balance_amount) > 0 ? `<tr><td style="${C}color:#c62828;font-weight:600">Bill Balance Due</td><td style="${C}text-align:right;font-family:monospace;font-weight:700;color:#c62828">${Number(bill.balance_amount).toFixed(2)}</td></tr>` : ''}
    </table>
    ${signatureFooter()}
  `;
  openPrintWindow('Payment Receipt', `Payment Receipt — ${fmtDate(payment.paid_at)}`, body);
}
